import React from "react";
import { ChatTabId } from "./ChatRoot";
import { BookOpenCheck, BookOpen, StickyNote } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

interface ChatTabsMobileProps {
  activeTab: ChatTabId;
  setActiveTab: (tab: ChatTabId) => void;
  allowedTabs?: ChatTabId[];
}

export const ChatTabsMobile: React.FC<ChatTabsMobileProps> = ({
  activeTab,
  setActiveTab,
  allowedTabs,
}) => {
  const { t } = useLanguage();

  const tabs: { id: ChatTabId; labelKey: string; icon: React.ReactNode; hidden?: boolean }[] = [
    {
      id: "chat",
      labelKey: "chatTabs.chat",
      icon: (
        <svg
          viewBox="0 0 256 256"
          className="h-4 w-4"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M128,20A108,108,0,0,0,31.85,177.23L21,209.66A20,20,0,0,0,46.34,235l32.43-10.81A108,108,0,1,0,128,20Zm0,192a84,84,0,0,1-42.06-11.27,12,12,0,0,0-6-1.62,12.1,12.1,0,0,0-3.8.62l-29.79,9.93,9.93-29.79a12,12,0,0,0-1-9.81A84,84,0,1,1,128,212Z"
          />
        </svg>
      ),
    },
    {
      id: "flashcards",
      labelKey: "chatTabs.flashcards",
      icon: (
        <svg
          viewBox="0 0 256 256"
          className="h-4 w-4"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M180,72H36A20,20,0,0,0,16,92V204a20,20,0,0,0,20,20H180a20,20,0,0,0,20-20V92A20,20,0,0,0,180,72Zm-4,128H40V96H176ZM240,52V176a12,12,0,0,1-24,0V56H64a12,12,0,0,1,0-24H220A20,20,0,0,1,240,52Z"
          />
        </svg>
      ),
    },
    {
      id: "quizzes",
      labelKey: "chatTabs.quizzes",
      icon: <BookOpenCheck className="h-4 w-4" />,
    },
    {
      id: "chapters",
      labelKey: "chatTabs.chapters",
      icon: <BookOpen className="h-4 w-4" />,
      hidden: true,
    },
    {
      id: "summary",
      labelKey: "chatTabs.summary",
      icon: <StickyNote className="h-4 w-4" />,
      hidden: true,
    },
    {
      id: "notes",
      labelKey: "chatTabs.notes",
      icon: (
        <svg
          viewBox="0 0 256 256"
          className="h-4 w-4"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M84,108A12,12,0,0,1,96,96h64a12,12,0,0,1,0,24H96A12,12,0,0,1,84,108Zm32,28H96a12,12,0,0,0,0,24h20a12,12,0,0,0,0-24ZM228,48V156.69a19.86,19.86,0,0,1-5.86,14.14l-51.31,51.31A19.86,19.86,0,0,1,156.69,228H48a20,20,0,0,1-20-20V48A20,20,0,0,1,48,28H208A20,20,0,0,1,228,48ZM52,204h92V156a12,12,0,0,1,12-12h48V52H52Zm139-36H168v23Z"
          />
        </svg>
      ),
    },
  ];

  const visibleTabs = tabs.filter((tab) => {
    // If allowedTabs is provided, use it as the source of truth (overrides hidden)
    if (allowedTabs) {
      return allowedTabs.includes(tab.id);
    }
    // Otherwise, respect the hidden property
    return !tab.hidden;
  });

  return (
    <div className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-primary/5 p-1 text-xs text-muted-foreground">
      <div className="flex w-full items-center justify-center overflow-x-auto scrollbar-hide">
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={[
                "flex flex-shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-1.5 font-medium transition-all",
                isActive
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {isActive ? (
                <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
              ) : (
                tab.icon
              )}
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
